import type { DataTableColumnDef } from 'components/tables/data-table';
import { InvoiceTotalsReportTotals } from 'types/reporting/finance';
import { t } from 'utils/getTranslations';
import { ReportLink, ReportNumberCell, TONED_COLUMN_META, tonedCell, GREEN, GREEN_OR_RED } from '../reportCells';
import { inBaseCurrency } from '../reportCurrency';

type InvoiceTotalsAmounts = {
    invoiceCount: number;
    totalWithoutVat: number | null;
    totalVat: number | null;
    totalWithVat: number | null;
    baseCurrency: string | null;
};

/** The count, net, VAT and gross columns both invoice totals reports end with; the amounts name the base currency. */
export function invoiceTotalsColumns<Row extends InvoiceTotalsAmounts>(
    rows: Row[] | undefined,
    currency: string | null
): DataTableColumnDef<Row, InvoiceTotalsReportTotals>[] {
    const amount = (id: 'totalWithoutVat' | 'totalVat' | 'totalWithVat', labelId: string) =>
        ({
            id,
            accessorKey: id,
            header: inBaseCurrency(t(labelId), currency),
            meta: {
                ...TONED_COLUMN_META,
                widthWeight: 0.9,
                minPx: 130,
                textAlign: 'right',
                nullHandling: 'nullsLast',
                renderTotal: (totals) => <ReportNumberCell value={totals?.[id]} />,
            },
            cell: tonedCell(rows, (row) => row[id], GREEN_OR_RED),
        }) satisfies DataTableColumnDef<Row, InvoiceTotalsReportTotals>;
    return [
        {
            id: 'invoiceCount',
            accessorKey: 'invoiceCount',
            header: t('reporting.column.invoice-count'),
            meta: {
                ...TONED_COLUMN_META,
                widthWeight: 0.6,
                minPx: 100,
                textAlign: 'right',
                renderTotal: (totals) => <ReportNumberCell value={totals?.invoiceCount} decimals={0} />,
            },
            cell: tonedCell(rows, (row) => row.invoiceCount, GREEN, 0),
        },
        amount('totalWithoutVat', 'reporting.column.total-without-vat'),
        amount('totalVat', 'reporting.column.total-vat'),
        amount('totalWithVat', 'reporting.column.total-with-vat'),
    ];
}

/** An invoice number that opens the invoice. */
export function ReportInvoiceLink({ invoiceId, children }: { invoiceId: number; children: React.ReactNode }) {
    return <ReportLink to={`/invoice/${invoiceId}`}>{children}</ReportLink>;
}
